import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { ActualFileObject, FilePondFile } from "filepond";
import { Bind } from "src/app/support/decorators/bind";
import { of } from "rxjs";
import { tap } from "rxjs/operators";
import { nextRuntimeId } from "src/app/support/runtime-id";
import { set, toString } from "lodash-es";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { FilePondDirective } from "src/app/modules/bootstrap/directives/filepond.directive";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";

@Component({
  selector: "snt-attachments",
  templateUrl: "attachments.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class AttachmentsComponent implements OnChanges {
  @ViewChild(FilePondDirective)
  filePondDirective: FilePondDirective | undefined;

  @Input()
  labelIdle: string | undefined;

  @Input()
  attachments: any[] | undefined;

  @Input()
  instantUpload: boolean = true;

  @Input()
  maxFiles: number = 10000;

  @Input()
  allowMultiple: boolean = true;

  @Input()
  acceptedFileTypes: string[] = ["application/pdf", "image/*"];

  @Input()
  allowFileSizeValidation: boolean = true;

  @Input()
  maxFileSize: string | null = null;

  @Input()
  maxTotalFileSize: string | null = null;

  @Output()
  added: EventEmitter<{
    id: string;
    file: File;
    size: number;
    name: string;
    origin: number;
  }> = new EventEmitter<{
    id: string;
    file: File;
    size: number;
    name: string;
    origin: number;
  }>();

  @Output()
  removed: EventEmitter<{
    id: string;
    file: File;
    size: number;
    name: string;
    origin: number;
  }> = new EventEmitter<{
    id: string;
    file: File;
    size: number;
    name: string;
    origin: number;
  }>();

  @Output()
  linked: EventEmitter<{
    storageId: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    callbackSetAttachmentId: (attachmentId: string) => void;
  }> = new EventEmitter();

  @Output()
  unLinked: EventEmitter<string> = new EventEmitter<string>();

  private storageBackend = inject(StorageBackend);

  ngOnChanges(changes: SimpleChanges): void {
    /*
    const attachmentsChange = changes["attachments"];
    if (!!attachmentsChange && !attachmentsChange.firstChange) {
      this.filePondDirective?.clearFiles();
      (<AttachmentV2[]>attachmentsChange.currentValue).forEach((attachment) => {
        this.filePondDirective?.addUploadedFile({
          name: attachment.name!,
          size: attachment.size!,
          type: "application/pdf"
        });
      });
    }

     */
  }

  @Bind
  onAddFile(filePondError: any | null, filePondEvent: FilePondFile): void {
    if (filePondError) {
      return;
    }

    this.added.emit({
      id: filePondEvent.id,
      file: <File>filePondEvent.file,
      size: filePondEvent.fileSize,
      name: filePondEvent.file.name,
      origin: filePondEvent.origin
    });
  }

  @Bind
  onRemoveFile(filePondError: any | null, filePondEvent: FilePondFile): void {
    if (filePondError) {
      return;
    }

    this.removed.emit({
      id: filePondEvent.id,
      file: <File>filePondEvent.file,
      size: filePondEvent.fileSize,
      name: filePondEvent.file.name,
      origin: filePondEvent.origin
    });
  }

  @Bind
  async onProcessFile(
    fieldName: string,
    file: ActualFileObject,
    metadata: Record<string, any>,
    load: (id: string) => void,
    error: (error: string) => void,
    progress: (
      isLengthComputable: boolean,
      loadedDataAmount: number,
      totalDataAmount: number
    ) => void
  ): Promise<void> {
    try {
      await this.storageBackend
        .upload(
          <File>file,
          (
            (_fileName: string, _fileSize: number, _fileType: string) =>
            (storageId) => {
              this.linked.emit({
                storageId,
                fileName: _fileName,
                fileSize: _fileSize,
                fileType: _fileType,
                callbackSetAttachmentId: (attachmentId: string) => {
                  set(file, "id", attachmentId);
                }
              });
              return of(null);
            }
          )(file.name, file.size, file.type)
        )
        .pipe(
          tap((status) => {
            progress(true, status.loaded, status.total);
          })
        )
        .toPromise();

      load(nextRuntimeId());
    } catch (ex) {
      error(toString(ex));
    }
  }

  async startUpload(): Promise<void> {
    await this.filePondDirective?.startUpload();
  }

  removeFile(id: string) {
    this.filePondDirective?.removeFile(id);
  }

  clearInvalidFiles(): void {
    this.filePondDirective?.clearInvalidFiles();
  }

  addUploadedFile({ name, size }: { name: string; size: number }): void {
    this.filePondDirective?.addUploadedFile({
      name,
      size,
      type: "application/pdf"
    });
  }
}
